export type GroupAlias = 'events' | 'stores' | 'groups' | 'tenants';
export type SiteType = 'portfolio' | 'location';
export type CourseStatus = 'not_started' | 'ongoing' | 'passed';

export interface SiteResponse {
  id: number;
  name: string;
  slug: string;
  logo: string;
  poster: string | null;
  primaryColor: string;
  secondaryColor: string;
  groupAlias: GroupAlias;
  hasGroups: boolean;
  features: Record<string, boolean> | null;
  siteType: SiteType;
}

export interface PaginatedMeta {
  nextCursor: string | null;
  hasMore: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginatedMeta;
}

export class BackendApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'BackendApiError';
  }
}

/**
 * Wrap an API call to return undefined for specific error statuses.
 * Useful for "get by id" calls where 404 means "not found".
 */
export async function orUndefined<T>(
  promise: Promise<T>,
  statuses: number[] = [404],
): Promise<T | undefined> {
  try {
    return await promise;
  } catch (e) {
    if (e instanceof BackendApiError && statuses.includes(e.status)) {
      return undefined;
    }
    throw e;
  }
}

/**
 * Wrap an API call to return an empty array for specific error statuses.
 * Useful for "list" calls where 401/404 means "no access" or "not found".
 */
export async function orEmptyArray<T>(
  promise: Promise<T[]>,
  statuses: number[] = [401, 404],
): Promise<T[]> {
  try {
    return await promise;
  } catch (e) {
    if (e instanceof BackendApiError && statuses.includes(e.status)) {
      return [];
    }
    throw e;
  }
}

/**
 * Wrap a paginated API call to extract .data and return empty array on error.
 * Useful for "list" endpoints that return PaginatedResponse.
 */
export async function paginatedOrEmpty<T>(
  promise: Promise<PaginatedResponse<T>>,
  statuses: number[] = [401],
): Promise<T[]> {
  try {
    const response = await promise;
    return response.data;
  } catch (e) {
    if (e instanceof BackendApiError && statuses.includes(e.status)) {
      return [];
    }
    throw e;
  }
}

/**
 * Handler invoked when a request returns 401 (unless skipAuthRedirect is set).
 * The app registers one via setUnauthorizedHandler to decide where to send the
 * user (e.g. navigate to a login route). Replaces the old server-side redirect.
 */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | undefined;
export function setUnauthorizedHandler(
  handler: UnauthorizedHandler | undefined,
) {
  onUnauthorized = handler;
}

interface BackendFetchOptions extends RequestInit {
  /** Skip automatic redirect to login on 401. Used by getMe() which expects 401 for unauthenticated users. */
  skipAuthRedirect?: boolean;
}

async function backendFetch<T>(
  endpoint: string,
  options?: BackendFetchOptions,
): Promise<T> {
  const url = `/api/v1${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const text = await response.text();

    // Notify the app on 401 (unless explicitly skipped)
    if (response.status === 401 && !options?.skipAuthRedirect) {
      onUnauthorized?.();
    }

    throw new BackendApiError(
      response.status,
      `Backend API error: ${response.status} ${text}`,
    );
  }

  // Handle empty responses (e.g., 204 No Content or empty body)
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text);
}

/**
 * Fetch from backend without authentication.
 * Used for public endpoints like code redemption flow.
 */
async function backendFetchPublic<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const url = `/api/v1${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new BackendApiError(
      response.status,
      `Backend API error: ${response.status} ${text}`,
    );
  }

  // Handle empty responses (e.g., 204 No Content or empty body)
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text);
}

export async function getPortfolios(): Promise<SiteResponse[]> {
  return backendFetch<SiteResponse[]>('/sites/portfolios');
}

export async function getPortfolio(id: number): Promise<SiteResponse> {
  return backendFetch<SiteResponse>(`/sites/portfolios/${id}`);
}

export async function createPortfolio(data: {
  name: string;
  slug: string;
}): Promise<SiteResponse> {
  return backendFetch<SiteResponse>('/sites/portfolios', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deletePortfolio(id: number): Promise<void> {
  await backendFetch<void>(`/sites/portfolios/${id}`, {
    method: 'DELETE',
  });
}

export async function getLocationsForPortfolio(
  portfolioId: number,
): Promise<SiteResponse[]> {
  return backendFetch<SiteResponse[]>(
    `/sites/portfolios/${portfolioId}/locations`,
  );
}

export async function getLocations(): Promise<SiteResponse[]> {
  return backendFetch<SiteResponse[]>('/sites/locations');
}

export async function getLocation(id: number): Promise<SiteResponse> {
  return backendFetch<SiteResponse>(`/sites/locations/${id}`);
}

export async function createLocation(data: {
  portfolioId: number;
  name: string;
  slug: string;
  templateId?: number;
}): Promise<SiteResponse> {
  return backendFetch<SiteResponse>('/sites/locations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteLocation(id: number): Promise<void> {
  await backendFetch<void>(`/sites/locations/${id}`, {
    method: 'DELETE',
  });
}

export async function getSiteBySlug(slug: string): Promise<SiteResponse> {
  return backendFetch<SiteResponse>(
    `/sites/by-slug/${encodeURIComponent(slug)}`,
  );
}

export async function updateSite(
  id: number,
  data: Partial<{
    name: string;
    slug: string;
    logo: string;
    poster: string | null;
    primaryColor: string;
    secondaryColor: string;
    groupAlias: GroupAlias;
    hasGroups: boolean;
    features: Record<string, boolean>;
  }>,
): Promise<SiteResponse> {
  return backendFetch<SiteResponse>(`/sites/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getAllLocations(): Promise<SiteResponse[]> {
  return backendFetch<SiteResponse[]>('/sites/locations/all');
}

// ============================================================================
// Email Templates API
// ============================================================================

export type EmailTemplateKey = 'invite' | 'magiclink';
export type Language = 'en' | 'sv' | 'no' | 'dk' | 'de' | 'nl' | 'fi' | 'fr';

export interface EmailTemplateResponse {
  id: number;
  key: EmailTemplateKey;
  siteId: number | null;
  siteName: string | null;
  language: Language;
  template: string;
  subject: string;
}

export interface EmailTemplateListItem {
  id: number;
  key: EmailTemplateKey;
  siteId: number | null;
  siteName: string | null;
  language: Language;
  subject: string;
}

export async function getEmailTemplates(
  siteId: number | null,
): Promise<EmailTemplateListItem[]> {
  const params = new URLSearchParams();
  if (siteId !== null) {
    params.set('siteId', String(siteId));
  }
  return backendFetch<EmailTemplateListItem[]>(`/email-templates?${params}`);
}

export async function getEmailTemplateById(
  id: number,
): Promise<EmailTemplateResponse> {
  return backendFetch<EmailTemplateResponse>(`/email-templates/${id}`);
}

export async function getEmailTemplateByKey(
  key: EmailTemplateKey,
  siteId: number | null,
  language: Language,
): Promise<EmailTemplateResponse> {
  const params = new URLSearchParams({
    key,
    language,
  });
  if (siteId !== null) {
    params.set('siteId', String(siteId));
  }
  return backendFetch<EmailTemplateResponse>(
    `/email-templates/by-key?${params}`,
  );
}

export async function createEmailTemplate(data: {
  key: EmailTemplateKey;
  siteId: number | null;
  language: Language;
  template: string;
  subject: string;
}): Promise<EmailTemplateResponse> {
  return backendFetch<EmailTemplateResponse>('/email-templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEmailTemplate(
  id: number,
  data: { template?: string; subject?: string },
): Promise<EmailTemplateResponse> {
  return backendFetch<EmailTemplateResponse>(`/email-templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteEmailTemplate(id: number): Promise<void> {
  await backendFetch<void>(`/email-templates/${id}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Site Templates API
// ============================================================================

export interface SiteTemplateResponse {
  id: number;
  createdAt: string;
  name: string;
  description: string | null;
  siteId: number;
  siteName: string;
  siteLogo: string;
  siteSlug: string;
}

export async function getSiteTemplates(): Promise<SiteTemplateResponse[]> {
  return backendFetch<SiteTemplateResponse[]>('/site-templates');
}

export async function getSiteTemplate(
  id: number,
): Promise<SiteTemplateResponse> {
  return backendFetch<SiteTemplateResponse>(`/site-templates/${id}`);
}

export async function createSiteTemplate(data: {
  name: string;
  description?: string;
  siteId: number;
}): Promise<SiteTemplateResponse> {
  return backendFetch<SiteTemplateResponse>('/site-templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteSiteTemplate(id: number): Promise<void> {
  await backendFetch<void>(`/site-templates/${id}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Students API
// ============================================================================

export interface CourseLocaleData {
  scorm: string;
  name: string;
  zip?: string;
  filename?: string;
}

export interface CourseWithStatus {
  sessionId: string;
  courseId: number;
  courseData: Record<string, CourseLocaleData>;
  status: CourseStatus;
  image: string | null;
  ordering: number | null;
}

export interface StudentResponse {
  id: number;
  personalCode: string;
  groupId: number;
  groupName: string;
  curriculumId: number;
  curriculumName: string;
  curriculumNameTranslated: Record<string, string> | null;
  locationId: number;
  siteSlug: string;
  progress: { passed: number; total: number };
  courses: CourseWithStatus[];
}

export async function getStudentByCode(code: string): Promise<StudentResponse> {
  return backendFetch<StudentResponse>(`/students/code/${code}`);
}

export async function deleteStudent(id: number): Promise<void> {
  await backendFetch<void>(`/students/${id}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Sessions API
// ============================================================================

export interface InlinedSurveyQuestion {
  id: number;
  beforeQuestion: Record<string, string>;
  afterQuestion: Record<string, string>;
  description: string;
  order: number;
}

export interface InlinedSurvey {
  id: number;
  name: string;
  courseId: number;
  questions: InlinedSurveyQuestion[];
  beforeCourseAnswered: boolean;
  afterCourseAnswered: boolean;
}

export interface SessionResponse {
  id: string;
  studentId: number;
  personalCode: string;
  courseId: number;
  curriculumId: number;
  locationId: number;
  groupId: number;
  status: CourseStatus;
  scormData: Record<string, unknown> | null;
  courseData: Record<string, unknown>;
  brandSlug: string;
  survey: InlinedSurvey | null;
}

export async function getSession(id: string): Promise<SessionResponse> {
  return backendFetch<SessionResponse>(`/sessions/${id}`);
}

export interface SaveSessionResponse {
  success: boolean;
  status: CourseStatus;
}

export async function saveSessionData(
  id: string,
  scormData: Record<string, unknown>,
): Promise<SaveSessionResponse> {
  return backendFetch<SaveSessionResponse>(`/sessions/${id}`, {
    method: 'POST',
    body: JSON.stringify({ scormData }),
  });
}

// ============================================================================
// Users API
// ============================================================================

export interface UserSiteRole {
  siteId: number;
  siteName: string;
  siteSlug: string;
  portfolioSlug: string | null;
  role: UserRole;
  canManageGroups: boolean;
  canManageUsers: boolean;
}

export interface UserResponse {
  id: number;
  name: string;
  email: string;
  roles: UserSiteRole[];
}

export async function getUsers(siteId: number): Promise<UserResponse[]> {
  return backendFetch<UserResponse[]>(`/users?siteId=${siteId}`);
}

export async function getUser(id: number): Promise<UserResponse> {
  return backendFetch<UserResponse>(`/users/${id}`);
}

export async function getUserByEmail(email: string): Promise<UserResponse> {
  return backendFetch<UserResponse>(
    `/users/by-email?email=${encodeURIComponent(email)}`,
  );
}

// Customer users are all 'member's; what they can change at a site comes from
// the permission flags. Every member can view the site.
export type UserRole = 'member' | 'super-admin';

export interface SitePermissions {
  canManageGroups: boolean;
  canManageUsers: boolean;
}

export async function createUser(
  data: {
    name: string;
    email: string;
    siteId: number;
  } & SitePermissions,
): Promise<UserResponse> {
  return backendFetch<UserResponse>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteUser(id: number, siteId?: number): Promise<void> {
  const params = siteId ? `?siteId=${siteId}` : '';
  await backendFetch<void>(`/users/${id}${params}`, {
    method: 'DELETE',
  });
}

export async function addUserRole(
  userId: number,
  data: { siteId: number } & SitePermissions,
): Promise<UserResponse> {
  return backendFetch<UserResponse>(`/users/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeUserRole(
  userId: number,
  siteId: number,
): Promise<UserResponse> {
  return backendFetch<UserResponse>(`/users/${userId}/roles/${siteId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Access matrix (user × location) API
// ============================================================================

// A single ladder the UI paints cells with. Maps to the two permission flags
// on a user_site row; 'none' removes the row entirely.
export type AccessLevel = 'none' | 'view' | 'groups' | 'users' | 'all';

export interface AccessMatrixLocation {
  id: number;
  name: string;
  slug: string;
  // Parsed from a trailing "(Region)" in the location name, if present.
  region: string | null;
}

export interface AccessMatrixUser {
  id: number;
  name: string;
  email: string;
  /** The caller may not change this user's access (they outrank the caller). */
  locked: boolean;
}

export interface AccessMatrixCell {
  userId: number;
  locationId: number;
  canManageGroups: boolean;
  canManageUsers: boolean;
}

export interface AccessMatrixResponse {
  portfolioId: number;
  locations: AccessMatrixLocation[];
  users: AccessMatrixUser[];
  cells: AccessMatrixCell[];
}

export interface AccessMatrixChange {
  userId: number;
  locationId: number;
  level: AccessLevel;
}

// Map a cell's flags to a level, and back. Kept next to the client so the SPA
// and the backend agree on the ladder.
export function levelFromFlags(
  canManageGroups: boolean,
  canManageUsers: boolean,
): AccessLevel {
  if (canManageGroups && canManageUsers) return 'all';
  if (canManageUsers) return 'users';
  if (canManageGroups) return 'groups';
  return 'view';
}

export function flagsFromLevel(level: AccessLevel): SitePermissions | null {
  switch (level) {
    case 'none':
      return null;
    case 'view':
      return { canManageGroups: false, canManageUsers: false };
    case 'groups':
      return { canManageGroups: true, canManageUsers: false };
    case 'users':
      return { canManageGroups: false, canManageUsers: true };
    case 'all':
      return { canManageGroups: true, canManageUsers: true };
  }
}

export async function getAccessMatrix(
  portfolioId: number,
): Promise<AccessMatrixResponse> {
  return backendFetch<AccessMatrixResponse>(
    `/users/access-matrix?portfolioId=${portfolioId}`,
  );
}

export async function updateAccessMatrix(
  portfolioId: number,
  changes: AccessMatrixChange[],
): Promise<AccessMatrixResponse> {
  return backendFetch<AccessMatrixResponse>('/users/access-matrix', {
    method: 'PUT',
    body: JSON.stringify({ portfolioId, changes }),
  });
}

// ============================================================================
// Groups API
// ============================================================================

export type GroupType = 'tenant' | 'supplier';

export interface GroupResponse {
  id: number;
  name: string;
  logo: string | null;
  /** Optional free-text unit ID. */
  unitId: string | null;
  locationId: number;
  userCreated: boolean;
  groupType: GroupType;
}

export async function getGroups(
  locationId: number,
  groupType?: GroupType,
): Promise<GroupResponse[]> {
  const params = new URLSearchParams({ locationId: String(locationId) });
  if (groupType) {
    params.set('groupType', groupType);
  }
  return backendFetch<GroupResponse[]>(`/groups?${params}`);
}

export async function getGroup(id: number): Promise<GroupResponse> {
  return backendFetch<GroupResponse>(`/groups/${id}`);
}

export async function createGroup(data: {
  locationId: number;
  name: string;
  logo?: string;
  groupType: GroupType;
}): Promise<GroupResponse> {
  return backendFetch<GroupResponse>('/groups', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateGroup(
  id: number,
  /** An empty `unitId` clears it. */
  data: { name?: string; logo?: string; unitId?: string },
): Promise<GroupResponse> {
  return backendFetch<GroupResponse>(`/groups/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteGroup(id: number): Promise<void> {
  await backendFetch<void>(`/groups/${id}`, {
    method: 'DELETE',
  });
}

export interface GroupWithStatsResponse {
  id: number;
  name: string;
  logo: string | null;
  unitId: string | null;
  locationId: number;
  userCreated: boolean;
  groupType: GroupType;
  completedCourses: number;
  /** Most recent last-active across the group's students; null when empty. */
  lastActive: string | null;
  /** Time spent on passed courses per curriculum, largest first. */
  timeSpent: { curriculumName: string; seconds: number }[];
}

export async function getGroupsWithStats(
  locationId: number,
  locale: string,
): Promise<GroupWithStatsResponse[]> {
  const params = new URLSearchParams({
    locationId: String(locationId),
    locale,
  });
  return backendFetch<GroupWithStatsResponse[]>(`/groups/with-stats?${params}`);
}

export async function searchGroups(
  locationId: number,
  query: string,
): Promise<GroupResponse[]> {
  const params = new URLSearchParams({
    locationId: String(locationId),
    query,
  });
  return backendFetch<GroupResponse[]>(`/groups/search?${params}`);
}

export async function upsertGroup(data: {
  locationId: number;
  name: string;
  logo?: string;
  groupType: GroupType;
  userCreated?: boolean;
}): Promise<GroupResponse> {
  return backendFetch<GroupResponse>('/groups/upsert', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function batchCreateGroups(
  groups: Array<{
    locationId: number;
    name: string;
    logo?: string;
    groupType: GroupType;
  }>,
): Promise<GroupResponse[]> {
  return backendFetch<GroupResponse[]>('/groups/batch', {
    method: 'POST',
    body: JSON.stringify({ groups }),
  });
}

export interface GroupStudentResponse {
  id: number;
  code: string | null;
  lastActive: string;
  curriculumName: string | null;
  curriculumNameTranslated: Record<string, string> | null;
  curriculumId: number | null;
  courseStatus: Record<string, string>;
}

export async function getStudentsForGroup(
  groupId: number,
  locale: string,
  all: boolean = false,
): Promise<GroupStudentResponse[]> {
  const params = new URLSearchParams({ locale });
  if (all) {
    params.set('all', 'true');
  }
  return backendFetch<GroupStudentResponse[]>(
    `/groups/${groupId}/students?${params}`,
  );
}

export interface CourseCompletionStats {
  courseId: number;
  courseName: string;
  count: number;
  [key: string]: unknown;
}

export interface CurriculumCompletionStats {
  curriculumId: number;
  curriculumName: string;
  count: number;
  [key: string]: unknown;
}

export interface GroupStatsResponse {
  students: number;
  completedPerCourse: CourseCompletionStats[];
  completedPerCurriculum: CurriculumCompletionStats[];
}

export async function getGroupStats(
  groupId: number,
  locale: string,
): Promise<GroupStatsResponse> {
  return backendFetch<GroupStatsResponse>(
    `/groups/${groupId}/stats?locale=${encodeURIComponent(locale)}`,
  );
}

/**
 * List groups for a location (public, no authentication required).
 * Used for code redemption flow.
 */
export async function getGroupsPublic(
  locationId: number,
  groupType?: GroupType,
): Promise<GroupResponse[]> {
  const params = new URLSearchParams({ locationId: String(locationId) });
  if (groupType) {
    params.set('groupType', groupType);
  }
  return backendFetchPublic<GroupResponse[]>(`/groups/public?${params}`);
}

/**
 * Search groups by name prefix (public, no authentication required).
 * Used for code redemption flow.
 */
export async function searchGroupsPublic(
  locationId: number,
  query: string,
): Promise<GroupResponse[]> {
  const params = new URLSearchParams({
    locationId: String(locationId),
    query,
  });
  return backendFetchPublic<GroupResponse[]>(`/groups/public/search?${params}`);
}

// ============================================================================
// Curriculums API
// ============================================================================

export interface CurriculumResponse {
  id: number;
  name: string;
  nameTranslated: Record<string, string> | null;
  locationId: number;
  courseCount: number;
}

export interface CurriculumCourse {
  courseId: number;
  courseName: string;
  order: number;
}

export interface CurriculumWithCoursesResponse {
  id: number;
  name: string;
  nameTranslated: Record<string, string> | null;
  locationId: number;
  courses: CurriculumCourse[];
}

export async function getCurriculums(
  locationId: number,
): Promise<CurriculumResponse[]> {
  return backendFetch<CurriculumResponse[]>(
    `/curriculums?locationId=${locationId}`,
  );
}

export async function getCurriculum(
  id: number,
): Promise<CurriculumWithCoursesResponse> {
  return backendFetch<CurriculumWithCoursesResponse>(`/curriculums/${id}`);
}

export async function createCurriculum(data: {
  name: string;
  nameTranslated?: Record<string, string> | null;
  locationId: number;
  courseIds?: number[];
}): Promise<CurriculumWithCoursesResponse> {
  return backendFetch<CurriculumWithCoursesResponse>('/curriculums', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCurriculum(
  id: number,
  data: { name?: string; nameTranslated?: Record<string, string> | null },
): Promise<CurriculumResponse> {
  return backendFetch<CurriculumResponse>(`/curriculums/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteCurriculum(id: number): Promise<void> {
  await backendFetch<void>(`/curriculums/${id}`, {
    method: 'DELETE',
  });
}

export async function addCourseToCurriculum(
  curriculumId: number,
  courseId: number,
  order?: number,
): Promise<CurriculumWithCoursesResponse> {
  return backendFetch<CurriculumWithCoursesResponse>(
    `/curriculums/${curriculumId}/courses`,
    {
      method: 'POST',
      body: JSON.stringify({ courseId, order }),
    },
  );
}

export async function removeCourseFromCurriculum(
  curriculumId: number,
  courseId: number,
): Promise<CurriculumWithCoursesResponse> {
  return backendFetch<CurriculumWithCoursesResponse>(
    `/curriculums/${curriculumId}/courses/${courseId}`,
    {
      method: 'DELETE',
    },
  );
}

export async function reorderCurriculumCourses(
  curriculumId: number,
  courseIds: number[],
): Promise<CurriculumWithCoursesResponse> {
  return backendFetch<CurriculumWithCoursesResponse>(
    `/curriculums/${curriculumId}/courses/order`,
    {
      method: 'PATCH',
      body: JSON.stringify({ courseIds }),
    },
  );
}

/**
 * Full course data within a curriculum (includes image and locale data).
 */
export interface FullCurriculumCourse {
  id: number;
  image: string | null;
  data: Record<string, CourseLocaleData>;
  order: number;
}

/**
 * Curriculum with full course data (image, data).
 */
export interface CurriculumWithFullCoursesResponse {
  id: number;
  name: string;
  nameTranslated: Record<string, string> | null;
  locationId: number;
  courses: FullCurriculumCourse[];
}

/**
 * Curriculum basic info (for re-export to client components).
 */
export interface Curriculum {
  id: number;
  name: string;
  nameTranslated: Record<string, string> | null;
}

/**
 * List curriculums with full course data (image, locale data).
 * Single efficient query instead of N+1.
 */
export async function getCurriculumsWithCourses(
  locationId: number,
): Promise<CurriculumWithFullCoursesResponse[]> {
  return backendFetch<CurriculumWithFullCoursesResponse[]>(
    `/curriculums/with-courses?locationId=${locationId}`,
  );
}

/**
 * Move a course up or down in a curriculum (atomic swap).
 * @param direction - positive to move down, negative to move up
 */
export async function moveCourseInCurriculum(
  curriculumId: number,
  courseId: number,
  direction: number,
): Promise<void> {
  await backendFetch<void>(
    `/curriculums/${curriculumId}/courses/${courseId}/move`,
    {
      method: 'PATCH',
      body: JSON.stringify({ direction }),
    },
  );
}

// ============================================================================
// Codes API
// ============================================================================

export type CodeType =
  | 'standard'
  | 'tenant_select'
  | 'supplier_select'
  | 'custom_supplier';

export interface CodeResponse {
  id: number;
  code: string;
  name: string | null;
  curriculumId: number;
  curriculumName: string;
  curriculumNameTranslated: Record<string, string> | null;
  groupId: number | null;
  groupName: string | null;
  locationId: number;
  codeType: CodeType;
  usageCount: number;
}

export async function getCodes(
  locationId: number,
  options?: {
    curriculumId?: number;
    groupId?: number;
    codeType?: CodeType;
  },
): Promise<CodeResponse[]> {
  const params = new URLSearchParams({ locationId: String(locationId) });
  if (options?.curriculumId) {
    params.set('curriculumId', String(options.curriculumId));
  }
  if (options?.groupId) {
    params.set('groupId', String(options.groupId));
  }
  if (options?.codeType) {
    params.set('codeType', options.codeType);
  }
  return backendFetch<CodeResponse[]>(`/codes?${params}`);
}

export async function getCodeById(id: number): Promise<CodeResponse> {
  return backendFetch<CodeResponse>(`/codes/${id}`);
}

export async function createCode(data: {
  curriculumId: number;
  locationId: number;
  codeType: CodeType;
  groupId?: number;
  name?: string;
}): Promise<CodeResponse> {
  return backendFetch<CodeResponse>('/codes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCode(
  id: number,
  data: { name: string | null },
): Promise<CodeResponse> {
  return backendFetch<CodeResponse>(`/codes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteCode(id: number): Promise<void> {
  await backendFetch<void>(`/codes/${id}`, {
    method: 'DELETE',
  });
}

// Redeem code types
export type RedeemCodeResponseType =
  | 'success'
  | 'select_group'
  | 'enter_supplier'
  | 'existing';

export interface RedeemSuccessResponse {
  type: 'success' | 'existing';
  personalCode: string;
  studentId: number;
  siteSlug: string;
}

export interface RedeemSelectGroupResponse {
  type: 'select_group';
  groups: Array<{ id: number; name: string }>;
  groupType: 'tenant' | 'supplier';
}

export interface RedeemEnterSupplierResponse {
  type: 'enter_supplier';
}

export type RedeemCodeResponse =
  | RedeemSuccessResponse
  | RedeemSelectGroupResponse
  | RedeemEnterSupplierResponse;

export async function redeemCode(data: {
  code: string;
  groupId?: number;
  groupName?: string;
}): Promise<RedeemCodeResponse> {
  return backendFetch<RedeemCodeResponse>('/codes/redeem', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Courses API
// ============================================================================

export interface CourseResponse {
  id: number;
  image: string | null;
  data: Record<string, CourseLocaleData>;
}

export interface CourseDetailResponse extends CourseResponse {
  locationIds: number[];
}

export async function getCourses(
  locationId: number,
): Promise<CourseResponse[]> {
  return backendFetch<CourseResponse[]>(`/courses?locationId=${locationId}`);
}

export async function getCourseById(id: number): Promise<CourseDetailResponse> {
  return backendFetch<CourseDetailResponse>(`/courses/${id}`);
}

export async function createCourse(data: {
  locationId: number;
  image?: string;
  data?: Record<string, CourseLocaleData>;
}): Promise<CourseDetailResponse> {
  return backendFetch<CourseDetailResponse>('/courses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCourse(
  id: number,
  data: { image?: string },
): Promise<CourseDetailResponse> {
  return backendFetch<CourseDetailResponse>(`/courses/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteCourse(id: number): Promise<void> {
  await backendFetch<void>(`/courses/${id}`, {
    method: 'DELETE',
  });
}

export async function setCourseLocale(
  courseId: number,
  locale: string,
  data: {
    name: string;
    scorm: string;
    zip?: string;
    filename?: string;
  },
): Promise<CourseDetailResponse> {
  return backendFetch<CourseDetailResponse>(
    `/courses/${courseId}/locales/${encodeURIComponent(locale)}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
  );
}

export async function removeCourseLocale(
  courseId: number,
  locale: string,
): Promise<CourseDetailResponse> {
  return backendFetch<CourseDetailResponse>(
    `/courses/${courseId}/locales/${encodeURIComponent(locale)}`,
    {
      method: 'DELETE',
    },
  );
}

export interface ProcessScormResponse {
  name: string;
  scormPath: string;
  launchData: string | null;
  course: CourseDetailResponse;
}

export async function processScormPackage(
  courseId: number,
  zipUrl: string,
  locale: string,
): Promise<ProcessScormResponse> {
  return backendFetch<ProcessScormResponse>(
    `/courses/${courseId}/process-scorm`,
    {
      method: 'POST',
      body: JSON.stringify({ zipUrl, locale }),
    },
  );
}

// ============================================================================
// Stats API
// ============================================================================

export interface BaseStats {
  groups: number;
  curriculums: number;
  courses: number;
  tenants: number;
  suppliers: number;
  tenantsWithoutPass: number;
  suppliersWithoutPass: number;
}

export interface EducationTimeEntry {
  /** Median seconds per session. */
  count: number;
  /** Total seconds over all sessions. */
  totalSeconds: number;
  courseName: string;
  curriculumName: string;
  locationName: string;
  [key: string]: unknown;
}

export interface SessionTimeSeriesEntry {
  count: number;
  period: string;
  curriculumName: string;
  locationName: string;
  [key: string]: unknown;
}

export interface StatsCourseInfo {
  id: number;
  name: string;
  image: string | null;
}

export interface StatsLocationInfo {
  id: number;
  slug: string;
  name: string;
  portfolioSlug: string | null;
}

export interface StatsResponse {
  fromDate: string;
  baseStats: BaseStats;
  educationTime: EducationTimeEntry[];
  sessions: SessionTimeSeriesEntry[];
  /**
   * The same period one year earlier (with `compare`; empty for `since`).
   * A period in progress is cut at the same date last year.
   */
  previousSessions: SessionTimeSeriesEntry[];
  courses: StatsCourseInfo[];
  /** Every curriculum at the requested locations (unfiltered). */
  curricula: CoverageCurriculum[];
  locations: StatsLocationInfo[];
}

export interface StatsParams {
  location: number[];
  course?: number[];
  /** Only sessions in these curricula (all when empty). */
  curriculum?: number[];
  period?: 'since' | 'year' | 'month' | 'day';
  step?: 'year' | 'month' | 'day';
  fromDate?: string;
  locale?: string;
  /** Also fetch the same period last year into `previousSessions`. */
  compare?: boolean;
  /** Only sessions of these groups (all groups when empty). */
  group?: number[];
  /** Only sessions of students who signed up with these codes. */
  code?: number[];
}

export async function getStats(params: StatsParams): Promise<StatsResponse> {
  const searchParams = new URLSearchParams();
  // Send as comma-separated to avoid serde_urlencoded "duplicate field" error
  if (params.location.length > 0) {
    searchParams.set('location', params.location.join(','));
  }
  if (params.course && params.course.length > 0) {
    searchParams.set('course', params.course.join(','));
  }
  if (params.curriculum && params.curriculum.length > 0) {
    searchParams.set('curriculum', params.curriculum.join(','));
  }
  if (params.period) searchParams.set('period', params.period);
  if (params.step) searchParams.set('step', params.step);
  if (params.fromDate) searchParams.set('fromDate', params.fromDate);
  if (params.locale) searchParams.set('locale', params.locale);
  if (params.compare) searchParams.set('compare', 'true');
  if (params.group && params.group.length > 0) {
    searchParams.set('group', params.group.join(','));
  }
  if (params.code && params.code.length > 0) {
    searchParams.set('code', params.code.join(','));
  }
  return backendFetch<StatsResponse>(`/stats?${searchParams.toString()}`);
}

// ============================================================================
// Coverage API
// ============================================================================

export interface CoverageGroup {
  id: number;
  name: string;
  groupType: GroupType;
  /** Latest passed session in any curriculum, ever; null when never. */
  lastCompleted: string | null;
}

export interface CoverageCurriculum {
  id: number;
  name: string;
}

export interface CoverageCell {
  groupId: number;
  curriculumId: number;
  /** Distinct students with a passed session in the period. */
  completed: number;
  /** Latest passed session in this curriculum, ever. */
  lastCompleted: string;
  /** Latest passed session in this curriculum in the period (null: none). */
  lastInPeriod: string | null;
}

export interface CoverageResponse {
  windowStart: string;
  windowEnd: string;
  groups: CoverageGroup[];
  curricula: CoverageCurriculum[];
  cells: CoverageCell[];
}

export interface CoverageParams {
  period?: 'since' | 'year' | 'month' | 'day';
  /** Reference date for the period (`YYYY-MM-DD`). */
  fromDate?: string;
  locale?: string;
  /** Only students who signed up with these codes (`getCoverage` only). */
  code?: number[];
  /**
   * Only completions in these curricula, and only groups expected to take
   * one of them (`getLocationCoverage` only).
   */
  curriculum?: number[];
}

function coverageSearch(locations: number[], params: CoverageParams) {
  const searchParams = new URLSearchParams();
  searchParams.set('location', locations.join(','));
  if (params.period) searchParams.set('period', params.period);
  if (params.fromDate) searchParams.set('fromDate', params.fromDate);
  if (params.locale) searchParams.set('locale', params.locale);
  if (params.code && params.code.length > 0) {
    searchParams.set('code', params.code.join(','));
  }
  if (params.curriculum && params.curriculum.length > 0) {
    searchParams.set('curriculum', params.curriculum.join(','));
  }
  return searchParams;
}

/**
 * Completions per group and curriculum in a period. Pairs with no
 * completion ever have no cell (their count is zero).
 */
export async function getCoverage(
  locations: number[],
  params: CoverageParams = {},
): Promise<CoverageResponse> {
  const searchParams = coverageSearch(locations, params);
  return backendFetch<CoverageResponse>(
    `/stats/coverage?${searchParams.toString()}`,
  );
}

export interface LocationCoverage {
  locationId: number;
  locationName: string;
  groupType: GroupType;
  /** Groups of this type at the location. */
  groups: number;
  /** Groups with no passed session in the period. */
  withoutCompletions: number;
}

export interface LocationCoverageResponse {
  windowStart: string;
  windowEnd: string;
  /** One entry per location and group type that has groups. */
  locations: LocationCoverage[];
}

/**
 * Per location: how many groups have no completions in a period. Portfolio
 * ids are expanded to their locations.
 */
export async function getLocationCoverage(
  locations: number[],
  params: CoverageParams = {},
): Promise<LocationCoverageResponse> {
  const searchParams = coverageSearch(locations, params);
  return backendFetch<LocationCoverageResponse>(
    `/stats/coverage/locations?${searchParams.toString()}`,
  );
}

// ============================================================================
// Super Stats API
// ============================================================================

export interface SuperSessionEntry {
  count: number;
  period: string;
  finished: string;
  locationName: string;
  portfolioName: string | null;
  [key: string]: unknown;
}

export interface SuperStatsResponse {
  fromDate: string;
  sessions: SuperSessionEntry[];
}

export interface SuperStatsParams {
  period?: 'since' | 'year' | 'month' | 'day';
  step?: 'year' | 'month' | 'day';
  fromDate?: string;
}

export async function getSuperStats(
  params: SuperStatsParams,
): Promise<SuperStatsResponse> {
  const searchParams = new URLSearchParams();
  if (params.period) searchParams.set('period', params.period);
  if (params.step) searchParams.set('step', params.step);
  if (params.fromDate) searchParams.set('fromDate', params.fromDate);
  return backendFetch<SuperStatsResponse>(
    `/stats/super?${searchParams.toString()}`,
  );
}

// ============================================================================
// Exports API
// ============================================================================

export interface ExportParams {
  location: number[];
  locale?: string;
}

export async function exportStudents(params: ExportParams): Promise<Response> {
  const searchParams = new URLSearchParams();
  // Send location IDs as comma-separated to avoid serde_urlencoded "duplicate field" error
  if (params.location.length > 0) {
    searchParams.set('location', params.location.join(','));
  }
  if (params.locale) searchParams.set('locale', params.locale);

  const url = `/api/v1/exports/students?${searchParams.toString()}`;

  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const text = await response.text();
    throw new BackendApiError(
      response.status,
      `Backend API error: ${response.status} ${text}`,
    );
  }

  return response;
}

export async function exportSessions(params: ExportParams): Promise<Response> {
  const searchParams = new URLSearchParams();
  // Send location IDs as comma-separated to avoid serde_urlencoded "duplicate field" error
  if (params.location.length > 0) {
    searchParams.set('location', params.location.join(','));
  }
  if (params.locale) searchParams.set('locale', params.locale);

  const url = `/api/v1/exports/sessions?${searchParams.toString()}`;

  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const text = await response.text();
    throw new BackendApiError(
      response.status,
      `Backend API error: ${response.status} ${text}`,
    );
  }

  return response;
}

export async function exportStatistics(
  params: ExportParams,
): Promise<Response> {
  const searchParams = new URLSearchParams();
  // Send location IDs as comma-separated to avoid serde_urlencoded "duplicate field" error
  if (params.location.length > 0) {
    searchParams.set('location', params.location.join(','));
  }
  if (params.locale) searchParams.set('locale', params.locale);

  const url = `/api/v1/exports/statistics?${searchParams.toString()}`;

  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const text = await response.text();
    throw new BackendApiError(
      response.status,
      `Backend API error: ${response.status} ${text}`,
    );
  }

  return response;
}

// ============================================================================
// Surveys API
// ============================================================================

export type FeedbackPosition = 'before_course' | 'after_course';

export interface FeedbackQuestionResponse {
  id: number;
  beforeQuestion: Record<string, string>;
  afterQuestion: Record<string, string>;
  createdAt: string;
  description: string;
}

export interface SurveyListItem {
  id: number;
  name: string;
  createdAt: string;
}

export interface SurveyQuestion {
  id: number;
  beforeQuestion: Record<string, string>;
  afterQuestion: Record<string, string>;
  description: string;
  order: number;
}

export interface SurveyResponse {
  id: number;
  name: string;
  createdAt: string;
  questions: SurveyQuestion[];
}

export interface AssignedSurveyResponse {
  id: number;
  name: string;
  createdAt: string;
  courseId: number;
  questions: SurveyQuestion[];
}

// Feedback Questions

export async function getFeedbackQuestions(): Promise<
  FeedbackQuestionResponse[]
> {
  return backendFetch<FeedbackQuestionResponse[]>('/surveys/questions');
}

export async function getFeedbackQuestion(
  id: number,
): Promise<FeedbackQuestionResponse> {
  return backendFetch<FeedbackQuestionResponse>(`/surveys/questions/${id}`);
}

export async function createFeedbackQuestion(data: {
  beforeQuestion: Record<string, string>;
  afterQuestion: Record<string, string>;
  description: string;
}): Promise<FeedbackQuestionResponse> {
  return backendFetch<FeedbackQuestionResponse>('/surveys/questions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFeedbackQuestion(
  id: number,
  data: {
    beforeQuestion?: Record<string, string>;
    afterQuestion?: Record<string, string>;
    description?: string;
  },
): Promise<FeedbackQuestionResponse> {
  return backendFetch<FeedbackQuestionResponse>(`/surveys/questions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteFeedbackQuestion(id: number): Promise<void> {
  await backendFetch<void>(`/surveys/questions/${id}`, {
    method: 'DELETE',
  });
}

// Surveys

export async function getSurveys(): Promise<SurveyListItem[]> {
  return backendFetch<SurveyListItem[]>('/surveys');
}

export async function getSurvey(id: number): Promise<SurveyResponse> {
  return backendFetch<SurveyResponse>(`/surveys/${id}`);
}

export async function createSurvey(data: {
  name: string;
}): Promise<SurveyListItem> {
  return backendFetch<SurveyListItem>('/surveys', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteSurvey(id: number): Promise<void> {
  await backendFetch<void>(`/surveys/${id}`, {
    method: 'DELETE',
  });
}

// Survey Questions linking

export async function addQuestionToSurvey(
  surveyId: number,
  questionId: number,
): Promise<void> {
  await backendFetch<void>(`/surveys/${surveyId}/questions`, {
    method: 'POST',
    body: JSON.stringify({ questionId }),
  });
}

export async function removeQuestionFromSurvey(
  surveyId: number,
  questionId: number,
): Promise<void> {
  await backendFetch<void>(`/surveys/${surveyId}/questions/${questionId}`, {
    method: 'DELETE',
  });
}

export async function updateSurveyQuestionOrder(
  surveyId: number,
  questionId: number,
  direction: number,
): Promise<void> {
  await backendFetch<void>(
    `/surveys/${surveyId}/questions/${questionId}/order`,
    {
      method: 'PATCH',
      body: JSON.stringify({ direction }),
    },
  );
}

// Survey Assignments

export async function assignSurveyToCourse(
  courseId: number,
  surveyId: number,
): Promise<void> {
  await backendFetch<void>(`/surveys/assignments/${courseId}`, {
    method: 'POST',
    body: JSON.stringify({ surveyId }),
  });
}

export async function removeSurveyAssignment(courseId: number): Promise<void> {
  try {
    await backendFetch<void>(`/surveys/assignments/${courseId}`, {
      method: 'DELETE',
    });
  } catch (error) {
    // Ignore 404 - nothing to remove is a valid state
    if (error instanceof BackendApiError && error.status === 404) {
      return;
    }
    throw error;
  }
}

export async function getAssignedSurvey(
  courseId: number,
  options?: { skipAuthRedirect?: boolean },
): Promise<AssignedSurveyResponse> {
  return backendFetch<AssignedSurveyResponse>(
    `/surveys/assignments/${courseId}`,
    options,
  );
}

// Survey Answers

export async function checkSurveyAnswered(
  sessionId: string,
  position: FeedbackPosition,
  options?: { skipAuthRedirect?: boolean },
): Promise<boolean> {
  const result = await backendFetch<{ answered: boolean }>(
    `/surveys/answers/check?sessionId=${encodeURIComponent(sessionId)}&position=${position}`,
    options,
  );
  return result.answered;
}

export async function saveSurveyAnswers(data: {
  surveyId: number;
  courseId: number;
  sessionId: string;
  position: FeedbackPosition;
  answers: Array<{ questionId: number; answer: number }>;
}): Promise<void> {
  await backendFetch<void>('/surveys/answers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Uploads API
// ============================================================================

export interface CreateUploadResponse {
  id: number;
  uploadUrl: string;
  key: string;
  publicUrl: string;
}

export interface FinalizeUploadResponse {
  success: boolean;
  url: string;
}

export async function createUpload(data: {
  filename: string;
  folder: string;
  tag: string;
  contentType?: string;
  metadata?: Record<string, unknown>;
}): Promise<CreateUploadResponse> {
  return backendFetch<CreateUploadResponse>('/uploads', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function finalizeUpload(
  id: number,
  hash?: string,
): Promise<FinalizeUploadResponse> {
  return backendFetch<FinalizeUploadResponse>(`/uploads/${id}/finalize`, {
    method: 'POST',
    body: JSON.stringify({ hash }),
  });
}

// =============================================================================
// Auth (Magic Link)
// =============================================================================

export interface RequestMagicLinkRequest {
  email: string;
  slug: string;
  language?: string;
}

export interface RequestMagicLinkResponse {
  success: boolean;
  message: string;
}

/**
 * Request a magic link email for login.
 * This is a public endpoint - no authentication required.
 */
export async function requestMagicLink(
  data: RequestMagicLinkRequest,
): Promise<RequestMagicLinkResponse> {
  return backendFetchPublic<RequestMagicLinkResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// =============================================================================
// Email Template Preview
// =============================================================================

export interface RenderMjmlRequest {
  template: string;
  subject: string;
  variables: Record<string, string>;
}

export interface RenderMjmlResponse {
  html: string;
  text: string;
  subject: string;
  errors: string[];
}

/**
 * Render an MJML template for preview.
 * Requires authentication (admin access).
 */
export async function renderMjmlPreview(
  data: RenderMjmlRequest,
): Promise<RenderMjmlResponse> {
  return backendFetch<RenderMjmlResponse>('/email-templates/render', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// =============================================================================
// Auth Token Exchange
// =============================================================================

export interface TokenExchangeResponse {
  success: boolean;
  redirectUrl: string;
}

export interface TokenExchangeResult {
  data: TokenExchangeResponse;
  setCookie: string | null;
}

/**
 * Exchange a magic link token for a session.
 * Returns both the response data and the Set-Cookie header for forwarding.
 */
export async function exchangeToken(
  token: string,
): Promise<TokenExchangeResult> {
  const url = `/api/v1/auth/token?token=${encodeURIComponent(token)}`;

  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new BackendApiError(
      response.status,
      `Backend API error: ${response.status} ${text}`,
    );
  }

  const data = (await response.json()) as TokenExchangeResponse;
  const setCookie = response.headers.get('set-cookie');

  return { data, setCookie };
}

// =============================================================================
// Current User
// =============================================================================

export interface MeResponse {
  id: number;
  email: string;
  name: string;
  superAdmin: boolean;
}

/**
 * Get the current authenticated user's info.
 * Returns undefined if not authenticated.
 */
export async function getMe(): Promise<MeResponse | undefined> {
  return orUndefined(
    backendFetch<MeResponse>('/auth/me', { skipAuthRedirect: true }),
    [401],
  );
}

/**
 * Get all sites the current user can access.
 * For super-admins: returns all sites with "super-admin" role.
 * For regular users: returns their specific role assignments.
 */
export async function getAccessibleSites(): Promise<UserSiteRole[]> {
  return backendFetch<UserSiteRole[]>('/auth/me/sites');
}

// ---------------------------------------------------------------------------
// Demo mode (super-admin)
// ---------------------------------------------------------------------------

/** Activity volume/shape generated for a demo location. */
export type DemoProfile = 'busy' | 'new-location' | 'seasonal';

export interface DemoLocationSummary {
  id: number;
  name: string;
  slug: string;
  students: number;
  sessions: number;
  groups: number;
}

export interface DemoStatusResponse {
  /** Whether the background refresh task is running on this server. */
  autoRefreshEnabled: boolean;
  retentionDays: number;
  locations: DemoLocationSummary[];
}

export interface DemoFillResponse {
  students: number;
  sessions: number;
}

export interface DemoRefreshResponse {
  shiftedSeconds: number;
  toppedUpStudents: number;
  prunedSessions: number;
  prunedFeedback: number;
}

/** Demo-mode status and the locations currently in demo mode. */
export async function getDemoStatus(): Promise<DemoStatusResponse> {
  return backendFetch<DemoStatusResponse>('/demo/status');
}

/** Flag a location as demo and fill it with activity (re-fills if already demo). */
export async function enableDemo(
  locationId: number,
  profile: DemoProfile,
): Promise<DemoFillResponse> {
  return backendFetch<DemoFillResponse>(`/demo/locations/${locationId}`, {
    method: 'POST',
    body: JSON.stringify({ profile }),
  });
}

/** Take a location out of demo mode: strip generated activity, clear the flag. */
export async function disableDemo(locationId: number): Promise<void> {
  await backendFetch<void>(`/demo/locations/${locationId}`, {
    method: 'DELETE',
  });
}

/** Run one refresh tick (re-anchor + top-up + prune) across all demo locations. */
export async function refreshDemo(): Promise<DemoRefreshResponse> {
  return backendFetch<DemoRefreshResponse>('/demo/refresh', {
    method: 'POST',
  });
}
